import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    CreateSubmodelDocument,
    type CreateSubmodelMutation,
    type CreateSubmodelMutationVariables,
} from '../../../../api/mutations/createSubmodel';
import { type LocalSubmodelSettings } from '../../../../api/types';
import useLoadingButton from '../../../../components/button/useLoadingButton';
import Form from '../../../../components/fields/Form';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import AddSubmodelDetails from './AddSubmodelDetails';
import useSubmodelValidator from './useSubmodelValidator';

type FormValues = LocalSubmodelSettings;

const initialValues: FormValues = {
    identifier: '',
    parentModelId: '',
    name: { defaultValue: '', overrides: [] },
    order: null,
    isActive: true,
    bodyType: null,
};

const AddSubmodel = () => {
    const { t } = useTranslation(['submodel']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validator = useSubmodelValidator();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            setLoading(true);
            message.loading({
                content: t('submodel:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });

            await apolloClient
                .mutate<CreateSubmodelMutation, CreateSubmodelMutationVariables>({
                    mutation: CreateSubmodelDocument,
                    variables: { settings: values },
                })
                .finally(() => {
                    // clean up
                    message.destroy('primary');
                    setLoading(false);
                });

            message.success({
                content: t('submodel:messages.creationSuccessful'),
                key: 'primary',
            });
            navigate('/admin/vehicles/submodels');
        },
        [setLoading, t, apolloClient, navigate]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="addSubmodelForm" htmlType="submit" type="primary">
                    {t('submodel:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/vehicles/submodels')}
            title={t('submodel:addTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form id="addSubmodelForm" name="addSubmodelForm" onSubmitCapture={handleSubmit}>
                        <AddSubmodelDetails initialValues={initialValues} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddSubmodel;
